import type { SelectHTMLAttributes } from 'react';

import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /**
   * Visually-hidden accessible label. Use when the control has no adjacent
   * visible `<label>` (e.g. a toolbar select whose value alone is shown).
   */
  label?: string;
  /** Convenience: render `<option>`s from data instead of passing children. */
  options?: SelectOption[];
  /** `sm` (default) is the compact toolbar pill; `md` reads as a page control. */
  size?: 'sm' | 'md';
}

/**
 * The single dropdown primitive for the app. A styled native `<select>` —
 * behaviour stays predictable and accessible across browsers — wearing the
 * shared pill chrome (subtle border + a custom inline-SVG chevron so the
 * indicator looks identical everywhere). Every `<select>` in the app should
 * go through here so they stay visually consistent.
 */
export function Select({
  label,
  options,
  size = 'sm',
  className,
  children,
  ...props
}: SelectProps) {
  const cls = [styles.select, size === 'md' ? styles.md : null, className]
    .filter(Boolean)
    .join(' ');
  return (
    <select className={cls} aria-label={label} {...props}>
      {options
        ? options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))
        : children}
    </select>
  );
}
